import type { RailwayService, RailwaySession } from '@cli-types/next-adapter/railway.js'
import { outputTail } from '@cli-utils/process.js'
import { isNonEmptyString } from '@cli-utils/shared.js'
import { spinner } from '@cli-utils/terminal.js'
import { parseRailwayJson } from '@next-adapter/init/railway/runner/parse-railway-json.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import { listRailwayServices } from './list-railway-services.js'
import { readRailwayServiceVariables } from './read-railway-service-variables.js'
import { waitForPostgresVariables } from './wait-for-postgres-variables.js'

export async function ensureRailwayPostgres(
  session: RailwaySession,
  cwd: string
): Promise<{ service: RailwayService; databaseUrl: string }> {
  const provisionSpinner = spinner()
  provisionSpinner.start('Creating your Railway Postgres database')
  try {
    const services = await listRailwayServices(session, cwd)
    const candidates = services.filter((service) => /^postgres(?:[-_ ].*)?$/i.test(service.name))

    for (const candidate of candidates) {
      const variables = await readRailwayServiceVariables(session, cwd, candidate.name)
      if (variables.DATABASE_PUBLIC_URL) {
        return { service: candidate, databaseUrl: variables.DATABASE_PUBLIC_URL }
      }
    }
    if (candidates.length > 0) {
      throw new Error(
        `Railway Postgres service ${candidates.map((candidate) => candidate.name).join(', ')} exists, but DATABASE_PUBLIC_URL is not available. Rerun init after the database is ready.`
      )
    }

    const result = await runRailway(session.runner, ['add', '--database', 'postgres', '--json'], {
      cwd,
      mode: 'capture',
      env: session.env
    })
    const payload = result.success
      ? parseRailwayJson<Record<string, unknown>>(result.stdout)
      : undefined
    const serviceName =
      payload && isNonEmptyString(payload.serviceName) ? payload.serviceName : null
    const serviceId = payload && isNonEmptyString(payload.serviceId) ? payload.serviceId : null
    if (!result.success || !serviceName) {
      throw new Error(
        outputTail(`${result.stdout}\n${result.stderr}`) ?? 'Could not provision Railway Postgres.'
      )
    }

    const service = { id: serviceId ?? serviceName, name: serviceName }
    provisionSpinner.message('Waiting for the database connection string')
    const variables = await waitForPostgresVariables(session, cwd, service)
    if (!variables.DATABASE_PUBLIC_URL) {
      throw new Error(
        `Railway created ${service.name}, but DATABASE_PUBLIC_URL was not available. Rerun init after the database is ready.`
      )
    }

    return { service, databaseUrl: variables.DATABASE_PUBLIC_URL }
  } finally {
    provisionSpinner.clear()
  }
}
